import { type QueryObserverResult, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { getAvailableTaskTypeOptions, getAvailableTaskTypes } from './task-types-client';
import type {
  AvailableTaskTypeOptionsResponse,
  AvailableTaskTypesResponse,
  UseAvailableTaskTypesInput,
} from './task-types-client.type';

/**
 * Query manager for the distinct task types available within a single
 * entity scope. The query stays disabled until `entityId` resolves to a
 * non-null value, so callers can render a default ("All work item types")
 * without juggling enablement themselves.
 *
 * @param {UseAvailableTaskTypesInput} input - Entity scope; `entityId` may be null while the active entity is still resolving
 * @param {UseQueryOptions<AvailableTaskTypesResponse>} options - Additional React Query options
 * @returns {{ taskTypes: AvailableTaskTypesResponse | undefined; query: QueryObserverResult<AvailableTaskTypesResponse> }} The list of types and its query result
 */
const useAvailableTaskTypes = (
  input: UseAvailableTaskTypesInput,
  options?: Omit<UseQueryOptions<AvailableTaskTypesResponse>, 'queryKey' | 'queryFn' | 'enabled'>,
): {
  taskTypes: AvailableTaskTypesResponse | undefined;
  query: QueryObserverResult<AvailableTaskTypesResponse>;
} => {
  const { entityType, entityId } = input;

  const query = useQuery({
    queryKey: ['available-task-types', entityType, entityId],
    queryFn: ({ signal }) => {
      if (!entityId) {
        return Promise.reject(new Error('Entity id is required'));
      }
      return getAvailableTaskTypes({ entityType, entityId }, signal);
    },
    enabled: Boolean(entityId),
    ...options,
  });

  return { taskTypes: query.data, query };
};

/**
 * Query manager for the labeled work-item type options available within a
 * single entity scope (`include_labels=true`). Option values are raw_type
 * keys suitable for the `task_types` filter param; labels carry the verbatim
 * external casing (equal to the value when no label is synced). The query
 * stays disabled until `entityId` resolves to a non-null value, so callers
 * can render a default ("All work item types") without juggling enablement
 * themselves.
 *
 * @param {UseAvailableTaskTypesInput} input - Entity scope; `entityId` may be null while the active entity is still resolving
 * @param {UseQueryOptions<AvailableTaskTypeOptionsResponse>} options - Additional React Query options
 * @returns {{ taskTypeOptions: AvailableTaskTypeOptionsResponse | undefined; query: QueryObserverResult<AvailableTaskTypeOptionsResponse> }} The labeled options and their query result
 */
const useAvailableTaskTypeOptions = (
  input: UseAvailableTaskTypesInput,
  options?: Omit<UseQueryOptions<AvailableTaskTypeOptionsResponse>, 'queryKey' | 'queryFn' | 'enabled'>,
): {
  taskTypeOptions: AvailableTaskTypeOptionsResponse | undefined;
  query: QueryObserverResult<AvailableTaskTypeOptionsResponse>;
} => {
  const { entityType, entityId } = input;

  const query = useQuery({
    queryKey: ['available-task-type-options', entityType, entityId],
    queryFn: ({ signal }) => {
      if (!entityId) {
        return Promise.reject(new Error('Entity id is required'));
      }
      return getAvailableTaskTypeOptions({ entityType, entityId }, signal);
    },
    enabled: Boolean(entityId),
    ...options,
  });

  return { taskTypeOptions: query.data, query };
};

export { useAvailableTaskTypeOptions, useAvailableTaskTypes };
